import React from 'react';
import {
  Container,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert
} from '@mui/material';
import {
  Security as SecurityIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';

const Privacy = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            mb: 2,
            fontFamily: '"Playfair Display", serif',
            fontWeight: 600,
            color: 'secondary.main'
          }}
        >
          <SecurityIcon sx={{ mr: 2, fontSize: 40 }} />
          Política de Privacidad
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Vinogenes — Tienda online de vinos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Última actualización: Mayo 2026
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sitio web: tienda.vinogenes.es
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 4 }}>
        En Vinogenes solo utilizamos tus datos personales para gestionar y enviar tus pedidos.
        No los usamos para publicidad, perfiles comerciales ni ningún otro fin.
      </Alert>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          1. Responsable del tratamiento
        </Typography>
        <Typography variant="body1" paragraph>
          El responsable del tratamiento de tus datos es <strong>Vinogenes</strong>, tienda online
          de vinos con sede en España. Puedes contactarnos en{' '}
          <strong>info@vinogenes.es</strong> o en el teléfono <strong>+34 653 56 29 30</strong>.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          2. Qué datos recogemos
        </Typography>
        <Typography variant="body1" paragraph>
          Únicamente recogemos los datos que nos facilitas al realizar un pedido en la tienda:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Nombre y apellidos" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Correo electrónico" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Teléfono de contacto" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Dirección postal de envío (calle, ciudad, código postal, provincia)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Detalle del pedido (vinos, cantidades, importes)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Notas del pedido, si las indicas voluntariamente" />
          </ListItem>
        </List>
        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          Los datos de tu tarjeta <strong>no los almacenamos nosotros</strong>: el pago se procesa
          de forma segura a través de <strong>Stripe</strong>, que actúa como encargado del
          tratamiento de los datos de pago.
        </Typography>
        <Typography variant="body1" paragraph>
          El carrito de compra puede guardarse de forma temporal en tu navegador (almacenamiento
          local) hasta que completes o abandones la compra. No usamos cookies de publicidad ni de
          seguimiento con fines comerciales.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          3. Para qué usamos tus datos
        </Typography>
        <Typography variant="body1" paragraph>
          Tus datos se utilizan <strong>exclusivamente</strong> para:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Gestionar tu pedido y tramitar el pago" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Preparar y enviar el pedido a la dirección indicada" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Ponernos en contacto contigo si es necesario por incidencias con el envío" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Enviarte el correo de confirmación del pedido" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Cumplir obligaciones legales aplicables (facturación, consumo, etc.)" />
          </ListItem>
        </List>
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>No utilizamos tus datos</strong> para newsletters, publicidad, estudios de
            mercado, cesión a terceros con fines comerciales ni creación de perfiles de usuario.
          </Typography>
        </Alert>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          4. Con quién compartimos datos
        </Typography>
        <Typography variant="body1" paragraph>
          Solo compartimos la información estrictamente necesaria con:
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Empresas de transporte y mensajería"
              secondary="Nombre, dirección y teléfono para entregar tu pedido"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Stripe"
              secondary="Procesamiento seguro del pago con tarjeta"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Proveedores técnicos (Firebase / Google Cloud)"
              secondary="Almacenamiento seguro de los datos del pedido"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Servicio de correo de confirmación"
              secondary="Solo para enviarte el email con el resumen del pedido"
            />
          </ListItem>
        </List>
        <Typography variant="body1" paragraph>
          No vendemos ni cedemos tus datos a terceros con fines distintos a los indicados.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          5. Conservación de los datos
        </Typography>
        <Typography variant="body1" paragraph>
          Conservamos los datos del pedido el tiempo necesario para gestionar la compra, las
          entregas, posibles reclamaciones y las obligaciones legales que nos correspondan. Pasado
          ese plazo, los datos se eliminarán o anonimizarán.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          6. Tus derechos
        </Typography>
        <Typography variant="body1" paragraph>
          Puedes ejercer en cualquier momento tus derechos de acceso, rectificación, supresión,
          limitación, oposición y portabilidad conforme al Reglamento General de Protección de Datos
          (RGPD), escribiendo a <strong>info@vinogenes.es</strong>. También tienes derecho a
          presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es).
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          7. Seguridad
        </Typography>
        <Typography variant="body1" paragraph>
          Aplicamos medidas técnicas razonables para proteger tu información: conexión cifrada
          (HTTPS), almacenamiento en infraestructura de Firebase y pagos gestionados por Stripe
          conforme a estándares de seguridad del sector.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          8. Contacto
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <EmailIcon sx={{ mr: 1, color: 'secondary.main' }} />
            <Typography variant="body1">Email: info@vinogenes.es</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PhoneIcon sx={{ mr: 1, color: 'secondary.main' }} />
            <Typography variant="body1">Teléfono: +34 653 56 29 30</Typography>
          </Box>
        </Box>
      </Box>

      <Alert severity="success" sx={{ mt: 4 }}>
        <Typography variant="body2">
          <strong>Resumen:</strong> Solo tratamos tus datos para enviar tus pedidos y cumplir la
          ley. Nada más.
        </Typography>
      </Alert>
    </Container>
  );
};

export default Privacy;
